/**
 * Highcharts v3 positioned the tooltip to the left of the point. Highcharts v6 changed the tooltip position to be
 * above the point. We want the old Highcharts v3 behavior, so I've extracted the Highcharts v3 tooltip positioning
 * logic from https://github.com/mixpanel/analytics/blob/336968c3a4d8bcde43ad534e250f8f4921bc21c0/media/plugins/highcharts/highcharts.src.es5.js#L8899-L8944
 */
export function getTooltipPosition({
  tooltipWidth,
  tooltipHeight,
  pointX,
  pointY,
  chartLeft,
  chartTop,
  chartWidth,
  chartHeight,
}) {
  const distance = 12;

  pointX = isNaN(pointX) ? 0 : pointX;
  let x = pointX + chartLeft - tooltipWidth - distance;
  let y = pointY - tooltipHeight + chartTop + 15; // 15 means the point is 15 pixels up from the bottom of the tooltip
  let alignedRight = false;

  // It is too far to the left, adjust it
  if (x < 7) {
    x = chartLeft + Math.max(pointX, 0) + distance;
  }

  // Test to see if the tooltip is too far to the right,
  // if it is, move it back to be inside and then up to not cover the point.
  if (x + tooltipWidth > chartLeft + chartWidth) {
    x -= x + tooltipWidth - (chartLeft + chartWidth);
    y = pointY - tooltipHeight + chartTop - distance;
    alignedRight = true;
  }

  // If it is now above the plot area, align it to the top of the plot area
  if (y < chartTop + 5) {
    y = chartTop + 5;

    // If the tooltip is still covering the point, move it below instead
    if (alignedRight && pointY >= y && pointY <= y + tooltipHeight) {
      y = pointY + chartTop + distance; // below
    }
  }

  // Now if the tooltip is below the chart, move it up. It's better to cover the
  // point than to disappear outside the chart. #834.
  if (y + tooltipHeight > chartTop + chartHeight) {
    y = Math.max(chartTop, chartTop + chartHeight - tooltipHeight - distance); // below
  }

  return {x, y};
}

// region Highcharts overrides

const MIN_LOG_ADJUSTMENT_THRESHOLD = 10;

export function logarithmicToLinear(num) {
  const isNegative = num < 0;
  let adjustedNum = Math.abs(num);
  if (adjustedNum < MIN_LOG_ADJUSTMENT_THRESHOLD) {
    adjustedNum += (MIN_LOG_ADJUSTMENT_THRESHOLD - adjustedNum) / MIN_LOG_ADJUSTMENT_THRESHOLD;
  }
  const result = Math.log(adjustedNum) / Math.LN10;
  return isNegative ? -result : result;
}

export function linearToLogarithmic(num) {
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  let result = Math.pow(10, absNum);
  if (result < MIN_LOG_ADJUSTMENT_THRESHOLD) {
    result = (MIN_LOG_ADJUSTMENT_THRESHOLD * (result - 1)) / (MIN_LOG_ADJUSTMENT_THRESHOLD - 1);
  }
  return isNegative ? -result : result;
}

// Based on http://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/samples/highcharts/yaxis/type-log-negative/
export function patchHighchartsForNegativeLog(Highcharts) {
  // Pass error messages
  Highcharts.Axis.prototype.allowNegativeLog = true;

  // Override conversions
  Highcharts.Axis.prototype.log2lin = function(num) {
    return logarithmicToLinear(num);
  };

  Highcharts.Axis.prototype.lin2log = function(num) {
    return linearToLogarithmic(num);
  };
}

// This is a workaround for a visual bug where thick series lines are clipped when they're at the edges of the plot area.
// Note that this workaround will also incorrectly disable out-of-bounds line clipping when zooming into plot axes.
// The bug is being tracked here: https://github.com/highcharts/highcharts/issues/3160
export function patchHighchartsForUnclippedLineAtPlotBounds(Highcharts) {
  Highcharts.wrap(Highcharts.Series.prototype, `drawGraph`, function(proceed) {
    proceed.call(this);
    if (this.graph) {
      // this.graph won't exist if the chart has no data
      this.graph.add(this.markerGroup);
    }
  });
}

// endregion
